import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface AdminUserRow {
  id: string;
  username: string;
  is_admin: boolean;
  created_at: string;
  last_login_at: string | null;
  favorites_count: number;
  saved_searches_count: number;
  blacklist_count: number;
}

interface AdminUserList {
  users: AdminUserRow[];
}

async function fetchUsers(): Promise<AdminUserList> {
  const res = await fetch("/api/v1/admin/users", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`users fetch failed: ${res.status}`);
  return res.json();
}

async function patchUser(userId: string, body: { is_admin: boolean }): Promise<AdminUserRow> {
  const res = await fetch(`/api/v1/admin/users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `patch failed: ${res.status}`);
  }
  return res.json();
}

async function deleteUser(userId: string): Promise<void> {
  const res = await fetch(`/api/v1/admin/users/${userId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `delete failed: ${res.status}`);
  }
}

export function useAdminUsers() {
  return useQuery<AdminUserList>({
    queryKey: ["admin", "users"],
    queryFn: fetchUsers,
    staleTime: 10_000,
  });
}

export function useToggleUserAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      patchUser(userId, { is_admin: isAdmin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}
